import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { Forbidden } from '@/modules/auth/Forbidden';
import { apiErrorMessage } from '@/shared/api-client';
import {
  ALLOWED_LOGO_TYPES,
  MAX_LOGO_BYTES,
  logoUrl,
  useBrand,
  useUpdateBrand,
  useUploadLogo,
} from './api';

export function BrandPage() {
  const { t } = useTranslation('common');
  const canManage = useAuthorization().can('franchise.brand.manage');

  const { data, isLoading, isError } = useBrand();
  const updateBrand = useUpdateBrand();
  const uploadLogo = useUploadLogo();

  const [displayName, setDisplayName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#000000');
  const [logoVersion, setLogoVersion] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data) {
      setDisplayName(data.displayName);
      setPrimaryColor(data.primaryColor);
    }
  }, [data]);

  if (!canManage) return <Forbidden />;
  if (isLoading) return <p className="p-6 text-muted-foreground">{t('brand.loading')}</p>;
  if (isError || !data)
    return <p className="p-6 text-destructive">{t('brand.errors.loadFailed')}</p>;

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateBrand.mutate(
      { displayName, primaryColor },
      {
        onSuccess: () => toast.success(t('brand.toast.updated')),
        onError: (error) => toast.error(apiErrorMessage(t, 'brand', error)),
      },
    );
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type as (typeof ALLOWED_LOGO_TYPES)[number])) {
      toast.error(t('brand.errors.unsupported_logo_type'));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(t('brand.errors.logo_too_large'));
      return;
    }
    uploadLogo.mutate(file, {
      onSuccess: () => {
        setLogoVersion((v) => v + 1);
        toast.success(t('brand.toast.logoUpdated'));
      },
      onError: (error) => toast.error(apiErrorMessage(t, 'brand', error)),
    });
  };

  return (
    <section className="grid max-w-2xl gap-8 p-6">
      <h1 className="text-2xl font-semibold">{t('brand.title')}</h1>

      <form className="grid gap-4" onSubmit={onSave}>
        <div className="grid gap-2">
          <Label htmlFor="brand-name">{t('brand.fields.displayName')}</Label>
          <Input
            id="brand-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="brand-color">{t('brand.fields.primaryColor')}</Label>
          <div className="flex items-center gap-3">
            <Input
              id="brand-color"
              type="color"
              className="h-10 w-16 p-1"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
            />
            <span className="text-sm text-muted-foreground">{primaryColor}</span>
          </div>
        </div>

        <Button type="submit" disabled={updateBrand.isPending} className="justify-self-start">
          {updateBrand.isPending ? t('brand.actions.saving') : t('brand.actions.save')}
        </Button>
      </form>

      <div className="grid gap-3">
        <Label>{t('brand.fields.logo')}</Label>
        <div className="flex items-center gap-4">
          {data.hasLogo ? (
            <img
              key={logoVersion}
              src={`${logoUrl()}?v=${logoVersion}`}
              alt={t('brand.logoAlt')}
              className="h-16 w-16 rounded border object-contain"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded border text-xs text-muted-foreground">
              {t('brand.noLogo')}
            </div>
          )}
          <input
            ref={fileInput}
            type="file"
            accept={ALLOWED_LOGO_TYPES.join(',')}
            className="hidden"
            data-testid="logo-input"
            onChange={onPickFile}
          />
          <Button
            type="button"
            variant="outline"
            disabled={uploadLogo.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {uploadLogo.isPending ? t('brand.actions.uploading') : t('brand.actions.uploadLogo')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t('brand.logoHint')}</p>
      </div>

      <div className="grid gap-2">
        <Label>{t('brand.preview')}</Label>
        <div
          className="flex items-center gap-3 rounded-md border p-4"
          style={{ borderColor: primaryColor }}
        >
          <span
            data-testid="preview-swatch"
            className="inline-block h-6 w-6 rounded-full"
            style={{ backgroundColor: primaryColor }}
          />
          <span className="font-semibold" style={{ color: primaryColor }}>
            {displayName || data.displayName}
          </span>
        </div>
      </div>
    </section>
  );
}
